import dateFormat from "dateformat";
import { GraphQLError } from "graphql";
import React from "react";
import { getProposalsByPropertiesQuery } from "rsl-api-106/lib/queries";
import {
  Criterias,
  ProposalByProperty,
  Query,
  QueryGetProposalsByPropertiesArgs,
} from "rsl-api-106/lib/types";

import { useApolloClient } from "@apollo/client";
import { useSearch, useSearchBar, useSession } from "@hooks";
import { addDaysToDate } from "@utils/dates";

import SearchBar, { SearchBarProps } from "./SearchBar";

interface SearchBarAppProps extends SearchBarProps {
  //
}

const SearchBarApp: React.FC<SearchBarAppProps> = ({
  isDateRange = true,
  showDuration = false,
  isMultiDestination = false,
  showChildren = true,
  showBabies = true,
}) => {
  const client = useApolloClient();
  const { sessionId } = useSession();
  const { setProposals, setCriterias, isSearching, setIsSearching } =
    useSearch();
  const {
    destinations,
    duration,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    roomCategory,
    product,
    nbAdults,
    nbChildren,
    nbBabies,
  } = useSearchBar();

  const handler = (e: React.FormEvent) => {
    e.preventDefault();
    if (sessionId) {
      setIsSearching(true);
      //Destination
      const selectedDestination =
        destinations == null
          ? null
          : Array.isArray(destinations)
          ? destinations.map((d) => parseInt(d.value))
          : [parseInt(destinations.value)];

      //Duration
      let selectedNbDays: number | null = null;
      let selectedStartDate: Date = new Date();
      let selectedEndDate: Date = new Date();

      if (showDuration) {
        selectedNbDays = duration ?? 7;
        selectedStartDate = startDate ? new Date(startDate) : new Date();
        selectedEndDate = new Date(
          addDaysToDate(selectedStartDate, selectedNbDays)
        );
      } else if (isDateRange) {
        //si pas de range de date défini, on prend startDate = date du jour et endDate = startDate + duration
        if (!startDate) {
          setStartDate(new Date());
          setEndDate(
            new Date(
              addDaysToDate(new Date(), showDuration && duration ? duration : 7)
            )
          );
        }

        selectedStartDate = startDate ? new Date(startDate) : new Date();
        selectedEndDate = endDate
          ? new Date(endDate)
          : new Date(
              addDaysToDate(
                selectedStartDate,
                showDuration && duration ? duration : 7
              )
            );

        if (!endDate) {
          setStartDate(selectedStartDate);
          setEndDate(selectedEndDate);
        }
      } else {
        //showDuration false & isDateRanger false
        selectedStartDate = startDate ? new Date(startDate) : new Date();
        selectedEndDate = endDate
          ? new Date(endDate)
          : new Date(
              addDaysToDate(
                selectedStartDate,
                showDuration && duration ? duration : 7
              )
            );
      }

      const criterias: Criterias = {
        nbDays: selectedNbDays,
        nbAdults: nbAdults,
        nbBabies: showBabies ? nbBabies : null,
        nbChildren1: showChildren ? nbChildren : null,
        nbChildren2: null,
        childrenBirthdate: null,
        propertyIds: selectedDestination,
        roomCategories: null,
        campaignCode: null,
        startDate: dateFormat(selectedStartDate, "yyyy-mm-dd"),
        endDate: dateFormat(selectedEndDate, "yyyy-mm-dd"),
        roomType: null,
        roomFeatureValues: null,
        privilegeCardID: null,
        partnerPrivilegeCardID: null,
        maxResults: 100,
        discountCode: null,
      };

      client
        .query<Query, QueryGetProposalsByPropertiesArgs>({
          query: getProposalsByPropertiesQuery(),
          variables: {
            session: { name: sessionId!! },
            input: {
              criterias: criterias,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          setProposals(
            response.data.getProposalsByProperties
              ?.proposalByProperty as ProposalByProperty[]
          );
          setCriterias(criterias);
        })
        .catch((err: GraphQLError) => {
          console.log(err);
        })
        .finally(() => {
          setIsSearching(false);
        });
    }
  };

  return (
    <SearchBar
      isDateRange={isDateRange}
      showDuration={showDuration}
      isMultiDestination={isMultiDestination}
      showChildren={showChildren}
      showBabies={showBabies}
      isSearching={isSearching}
      handler={handler}
    />
  );
};

export default SearchBarApp;
